import type { ObjectActionHandlers } from "@/object-actions/types";
import type { Entity } from "@cohesive/ui-components";
import { getEntityData } from "@cohesive/ui-components";
import { useMemo } from "react";
import toast from "react-hot-toast";

export interface EntityNodeActionHandlerDeps {
  entity: Entity | null | undefined;
  canEdit: boolean;
  onDelete: () => void;
  onOpen: () => void;
  onOpenInNewTab?: () => void;
}

async function copyTextToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
  toast.success("URL copied to clipboard");
}

export function createEntityNodeActionHandlers(
  deps: EntityNodeActionHandlerDeps,
): ObjectActionHandlers {
  const entity = deps.entity;
  if (!entity) {
    return {};
  }

  const handlers: ObjectActionHandlers = {
    delete: deps.canEdit ? () => deps.onDelete() : undefined,
    open: () => deps.onOpen(),
    "open-in-new-tab": deps.onOpenInNewTab
      ? () => deps.onOpenInNewTab?.()
      : undefined,
  };

  if ("base" in entity && entity.base === "file") {
    const entityData = getEntityData(entity) as Record<string, unknown>;
    const externalUrl = entityData?.web_link as string | undefined;
    if (externalUrl) {
      handlers["copy-url"] = () => {
        void copyTextToClipboard(externalUrl).catch(() => {
          toast.error("Failed to copy URL");
        });
      };
    }
  }

  const hasTypeSpecificCopyUrl = "base" in entity && entity.base === "file";

  if (!hasTypeSpecificCopyUrl) {
    const gd = getEntityData(entity) as Record<string, unknown>;
    const resolvedUrl =
      (gd?.web_link as string | undefined) ??
      (gd?.html_link as string | undefined) ??
      (gd?.granola_url as string | undefined) ??
      (gd?.url as string | undefined);

    if (resolvedUrl && !handlers["copy-url"]) {
      handlers["copy-url"] = () => {
        void copyTextToClipboard(resolvedUrl).catch(() => {
          toast.error("Failed to copy URL");
        });
      };
    }
  }

  return handlers;
}

export function useEntityNodeObjectActionHandlers(
  deps: EntityNodeActionHandlerDeps,
): ObjectActionHandlers {
  return useMemo(() => createEntityNodeActionHandlers(deps), [deps]);
}
